// app/javascript/components/Sidebar.js
import React, { useState, useEffect, forwardRef } from 'react';
import ReportModal from './ReportModal';

const Sidebar = forwardRef((props, ref) => {
    const { openModal, accessLevel, allDagNodes, diagramId, diagramName, isNewDiagram, organisationName } = props;
    const [isReportModalOpen, setReportModalOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [defaultViewing, setDefaultViewing] = useState(null);

    const toggleSidebar = () => {
        setSidebarOpen(prevState => !prevState);
    };

    const getDiagramSlug = () => {
        // Split the pathname by '/' and get the last segment
        const pathSegments = window.location.pathname.split('/');
        return pathSegments[pathSegments.length - 1];
    };

    // Fetch default viewing type when sidebar opens
    useEffect(() => {
        if (sidebarOpen) {
            const slug = getDiagramSlug();
            if (!slug) return;
            fetch(`/diagrams/${slug}/default_viewing`)
                .then(res => res.ok ? res.json() : Promise.reject())
                .then(data => setDefaultViewing(data.default_viewing))
                .catch(() => setDefaultViewing(null));
        }
    }, [sidebarOpen]);

    const saveDefaultView = (viewOption) => {
        const csrfToken = document.querySelector("[name=csrf-token]").content;
        var slug = getDiagramSlug();
        if (!slug) return;

        window.showLoading();

        fetch(`/diagrams/${slug}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken
            },
            body: JSON.stringify({
                diagram: {
                    viewing_default: viewOption
                },
            })
        })
        .then(response => {
            if (response.ok) {
                setDefaultViewing(viewOption); // update UI immediately
                alert('Default view saved successfully');
                window.hideLoading();
            } else {
                throw new Error('Failed to save default view');
            }
        })
        .catch(error => {
            console.error('Error:', error);
            alert('Failed to save default view');
            window.hideLoading();
        });
    };

    return (
        <>
            {/* Blur Overlay - Render this directly in the component */}
            <div 
                onClick={sidebarOpen ? toggleSidebar : undefined}
                className={`fixed inset-0 transition-all duration-300 ${sidebarOpen ? 'backdrop-blur-md pointer-events-auto' : 'pointer-events-none'}`}
                style={{ 
                    opacity: sidebarOpen ? 1 : 0,
                    zIndex: 15
                }}
                id="sidebar-blur-overlay"
            />

            {/* Sidebar Toggle Button */}
            <span
                className="absolute text-white text-4xl top-12 left-12 cursor-pointer transition-transform duration-300"
                onClick={toggleSidebar}
                style={{ 
                    color: '#6A6E70',
                    transform: sidebarOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    zIndex: 20
                }}
            >
                <i className="bi bi-arrow-bar-right px-2 rounded-md"></i>
            </span>

            {/* Sidebar - use React state to control visibility */}
            <div
                ref={ref}
                className={`sidebar fixed top-0 bottom-0 lg:left-0 p-2 overflow-y-auto text-center transform transition-transform duration-300 ${sidebarOpen ? '' : '-translate-x-full'}`}
                style={{ 
                    backgroundColor: 'white', // Add solid background color
                    zIndex: 25, // Increase z-index to be higher than the toggle button
                    minWidth: 'fit-content',
                    maxWidth: '30%',
                    boxShadow: sidebarOpen ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none' // Optional: add shadow for better separation
                }}
            >
                <div className="text-xl">
                    <div className="px-4 p-2.5 mt-8 mb-8 flex items-center justify-between">
                        {/* <i className="bi bi-list px-2 py-1 rounded-md bg-mainbggreen"></i> */}
                        <h1 className="text-[20px]">ordit</h1>
                        <i
                            className="bi bi-arrow-bar-left cursor-pointer"
                            onClick={toggleSidebar}
                        ></i>
                    </div>
                </div>
                
                <div
                    className="p-2.5 mt-3 flex items-center rounded-md px-4 duration-300 cursor-pointer hover:underline" 
                    onClick={() => window.location.href="/"}
                >
                    <i className="bi bi-house-door-fill"></i>
                    <span className="text-[15px] ml-4">Home</span>
                </div>
                
                {(accessLevel > 1) && <>
                    <div className="my-4 bg-gray-600 h-[1px]"></div>
                    </>
                }

                {(accessLevel === 4) && (<>
                    <div
                        className="p-2.5 mt-3 flex items-center rounded-md px-4 duration-300 cursor-pointer hover:underline" onClick={() => openModal({
                            name: '',
                            description: '',
                            duedate: '',
                            completed_at: '',
                            notes: '',
                            optional: false,
                            parentIds: [],
                            diagram_Id: diagramId,
                        })}
                    >
                    <i className="bi bi-node-plus"></i>
                        <span className="text-[15px] ml-4">Create Node</span>
                    </div>
                </>
                )}

                {(accessLevel > 1) && (<>
                    <div className="p-2.5 mt-3 flex items-center rounded-md px-4 duration-300 cursor-pointer hover:underline" onClick={() => openModal(null)}>
                        <i className="bi bi-wrench-adjustable"></i>
                        <span className="text-[15px] ml-4">Edit Node</span>
                    </div>
                    </>
                )}
                
                <div className="my-4 bg-gray-600 h-[1px]"></div>

                {(accessLevel == 4) && (<>
                    <div
                        className="p-2.5 mt-3 flex items-center rounded-md px-4 duration-300 cursor-pointer hover:underline" 
                        onClick={() => window.location.href="/diagrambuilder"}
                    >
                        <i className="bi bi-stack"></i>
                        <span className="text-[15px] ml-4">Diagram Builder</span>
                        <i className="ml-2 bi bi-arrow-up-left-square"></i>
                    </div>

                    <div className="my-4 bg-gray-600 h-[1px]"></div>

                    <div
                        className="p-2.5 mt-3 flex items-center rounded-md px-4 duration-300 cursor-pointer hover:underline" onClick={() => setReportModalOpen(true)}
                    >
                        <i className="bi bi-file-earmark-bar-graph"></i>
                        <span className="text-[15px] ml-4">Reports</span>
                    </div>
                    </>
                )}

                <div className="my-4 bg-gray-600 h-[1px]"></div>

                <div
                    className="p-2.5 mt-3 flex items-center rounded-md px-4 duration-300 cursor-pointer hover:underline"
                    onClick={() => {
                        const submenu = document.querySelector("#dropdownSubmenu");
                        const arrow = document.querySelector("#dropdownArrow");
                        if (submenu && arrow) {
                            submenu.classList.toggle("dropdown-expanded");
                            arrow.classList.toggle("rotate-180");
                        }
                    }}
                >
                    <i className="bi bi-diagram-2"></i>
                    <div className="flex justify-between w-full items-center">
                        <span className="text-[15px] ml-4">Diagram Views</span>
                        <span className="text-sm" id="dropdownArrow">
                            <i className="bi bi-chevron-down"></i>
                        </span>
                    </div>
                </div>
                <div
                    className="overflow-hidden transition-all duration-300 max-h-0 text-left text-sm mt-2 w-4/5 mx-auto"
                    id="dropdownSubmenu"
                >
                    {["All", "ExcludeCompleted", "ExcludeOptional", "ExcludeCompletedAndOptional"].map(option => (
                        <div key={option} className="flex items-center justify-between p-2 hover:bg-gray-100 rounded-md mt-1">
                            <h1 
                                onClick={() => {window.location.href = window.location.href.split('?')[0] + `?show=${option}`;}}
                                className="cursor-pointer hover:underline flex-grow"
                            >
                                {option.replace(/([A-Z])/g, ' $1').trim()}
                            </h1>
                            {accessLevel >= 2 && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        saveDefaultView(option);
                                    }}
                                    className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded-md"
                                    title="Set as default view"
                                >
                                    <i className={`bi ${defaultViewing === option ? "bi-bookmark-plus-fill" : "bi-bookmark-plus"}`}></i>
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                <div className="my-4 bg-gray-600 h-[1px]"></div>
                
                <div
                    className="p-2.5 mt-3 flex items-center rounded-md px-4 duration-300 cursor-pointer hover:underline" onClick={() => window.open("/documentation", "_blank")}
                >
                        <i className="bi bi-window-plus"></i>
                        <span className="text-[15px] ml-4">Documentation</span>
                </div>

                <div className="my-4 bg-gray-600 h-[2px]"></div>
                
                <form
                    method="POST"
                    action="/user/sign_out"
                    className="mt-8 btn btn-blue block rounded-full hover:underline"
                    style={{ display: 'block' }}
                    onSubmit={e => {
                        // Rails expects a hidden _method input for DELETE
                        // and turbo_method for Turbo
                        // CSRF token is handled by Rails UJS or meta tag
                    }}
                >
                    <input type="hidden" name="_method" value="delete" />
                    <input type="hidden" name="turbo_method" value="delete" />
                    <button type="submit" className="p-2.5 mt-3 flex items-center rounded-md px-4 duration-300 cursor-pointer hover:underline">
                        <i className="bi bi-box-arrow-in-right"></i>
                        <span className="text-[15px] ml-4">Sign out</span>
                    </button>
                </form>
            </div>
            {(accessLevel == 4) && (<>
                <ReportModal closeReportModal={() => setReportModalOpen(false)} isReportModalOpen={isReportModalOpen} allDagNodes={allDagNodes}></ReportModal>
                </>
            )}
        </>
    );
});

export default Sidebar;